import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const LAST_UPDATED = 'April 29, 2026';
const COMPANY = 'HealthClear AI, LLC';
const EMAIL = 'privacy@healthclearai.ai';
const ADDRESS = '6900 Tavistock Lakes Blvd. Suite 400, Orlando FL, 32827';

function Section({ id, title, children }) {
  return (
    <section id={id} className="mb-10">
      <h2 className="text-lg font-bold text-slate-800 dark:text-white mb-3 pb-2 border-b border-slate-100 dark:border-slate-700">{title}</h2>
      <div className="space-y-3 text-slate-600 dark:text-slate-300 text-sm leading-relaxed">{children}</div>
    </section>
  );
}

export default function PrivacyPage() {
  const { dark } = useTheme();
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="bg-white dark:bg-slate-900 border-b border-slate-100 dark:border-slate-800 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/">
            <img src={dark ? '/logo-dark.svg?v=3' : '/logo.svg?v=3'} alt="HealthClear AI" className="h-9 w-auto" />
          </Link>
          <Link to="/" className="text-sm text-slate-500 dark:text-slate-400 hover:text-sky-600 transition-colors">← Back to Home</Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title block */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Privacy Policy</h1>
          <p className="text-sm text-slate-400 dark:text-slate-500">Last updated: {LAST_UPDATED} &nbsp;·&nbsp; Effective: {LAST_UPDATED}</p>
          {/* CARIN badge */}
          <div className="mt-4 inline-flex items-center gap-2 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 dark:border-sky-800 text-sky-700 dark:text-sky-300 text-xs font-medium px-3 py-1.5 rounded-full">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            Aligned with the CARIN Code of Conduct
          </div>
        </div>

        {/* TOC */}
        <nav className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 mb-10">
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-3">Contents</p>
          <ol className="list-decimal list-inside space-y-1 text-sm text-sky-600">
            {[
              ['who-we-are', 'Who We Are'],
              ['data-we-collect', 'Data We Collect'],
              ['health-plan-data', 'Health Plan Data (via Flexpa)'],
              ['how-we-use', 'How We Use Your Data'],
              ['no-selling', 'We Do Not Sell Your Health Data'],
              ['sharing', 'When We Share Data'],
              ['retention', 'Data Retention'],
              ['your-rights', 'Your Rights'],
              ['security', 'Security'],
              ['hipaa', 'HIPAA Notice'],
              ['children', "Children's Privacy"],
              ['changes', 'Changes to This Policy'],
              ['contact', 'Contact Us'],
            ].map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="hover:text-sky-800 transition-colors">{label}</a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Intro */}
        <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-8">
          {COMPANY} ("<strong>HealthClear AI</strong>", "we", "us", or "our") operates <strong>healthclearai.ai</strong> and related mobile applications. This Privacy Policy explains how we collect, use, disclose, and protect your information — including your health plan and medical records data — when you use our services. We are committed to transparency and have aligned this policy with the <a href="https://www.carinalliance.com/our-work/trust-framework-and-code-of-conduct/" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline">CARIN Alliance Code of Conduct</a>.
        </p>

        <Section id="who-we-are" title="1. Who We Are">
          <p>{COMPANY} provides an AI-powered health records analysis platform that helps individuals understand their medical documents in plain language. We are the data controller for personal information collected through our services.</p>
          <p><strong>Contact:</strong> {EMAIL} &nbsp;·&nbsp; {ADDRESS}</p>
        </Section>

        <Section id="data-we-collect" title="2. Data We Collect">
          <p>We collect the following categories of information:</p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Account information:</strong> Name, email address, password (hashed), preferred language/reading level.</li>
            <li><strong>Uploaded documents:</strong> Medical reports, lab results, and other health documents you choose to upload for analysis.</li>
            <li><strong>Health plan data:</strong> Claims, eligibility, benefits, and clinical data retrieved from your health insurer — only when you explicitly authorize a connection (see Section 3).</li>
            <li><strong>Usage data:</strong> Pages visited, features used, session timestamps, and error logs. We do not link usage data to your health records.</li>
            <li><strong>Payment information:</strong> Billing is processed by Stripe. We store only the last-4 digits of your card and subscription status — never full card numbers.</li>
            <li><strong>Communications:</strong> Emails you send us and support interactions.</li>
          </ul>
        </Section>

        <Section id="health-plan-data" title="3. Health Plan Data (via Flexpa)">
          <p>
            When you choose to connect your health plan, we use <strong>Flexpa</strong> (flexpa.com) as our authorized data access service. Flexpa is a HIPAA-compliant, SOC 2 Type II certified third party that retrieves your health plan records using the FHIR Patient Access API, which federally mandated payers are required to provide under the CMS Interoperability and Patient Access Final Rule.
          </p>
          <p><strong>What this means for you:</strong></p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li>You will be shown a clear consent screen identifying exactly what data will be retrieved before any connection is made.</li>
            <li>You must actively check a box and click "Connect" — we never initiate retrieval without your explicit action.</li>
            <li>You can revoke access at any time from your Profile settings, which will stop future data retrieval and delete your cached health plan data.</li>
            <li>Flexpa maintains a temporary cached copy of your data solely to improve retrieval reliability — it is not used for any other purpose.</li>
            <li>We only request the minimum data necessary to provide the analysis features you have enabled ("<strong>data minimization</strong>").</li>
          </ul>
          <p>
            Flexpa's privacy policy is available at <a href="https://flexpa.com/privacy" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline">flexpa.com/privacy</a>. We have a Business Associate Agreement (BAA) with Flexpa in place.
          </p>
        </Section>

        <Section id="how-we-use" title="4. How We Use Your Data">
          <p>We use your information <strong>only</strong> for the following purposes:</p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Providing the service:</strong> Analyzing your medical documents and health plan data to generate plain-language summaries, trend reports, and care recommendations.</li>
            <li><strong>Account management:</strong> Authentication, session management, and subscription billing.</li>
            <li><strong>Transactional communications:</strong> Password resets, appointment reminders (if enabled), and billing receipts.</li>
            <li><strong>Product improvement:</strong> Aggregate, de-identified analytics to improve our AI models and features. Your individually identifiable health data is never used to train AI models without your separate, explicit consent.</li>
            <li><strong>Legal compliance:</strong> Responding to lawful requests, enforcing our terms, and protecting rights and safety.</li>
          </ul>
          <p>We do <strong>not</strong> use your health data for advertising, employer reporting, credit scoring, or any purpose you have not authorized.</p>
        </Section>

        <Section id="no-selling" title="5. We Do Not Sell Your Health Data">
          <p className="font-semibold text-slate-800">HealthClear AI does not sell, rent, lease, or otherwise commercially exploit your personal health information or health plan data to any third party — ever.</p>
          <p>This commitment applies regardless of where you live, including under California's CCPA/CPRA, Virginia's CDPA, and other state privacy laws.</p>
        </Section>

        <Section id="sharing" title="6. When We Share Data">
          <p>We share your information only in these limited circumstances:</p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Service providers:</strong> Vendors who help us operate the platform — including Flexpa (health plan connectivity), Stripe (payments), Brevo (transactional email), and Microsoft Azure (cloud infrastructure). All have signed data processing agreements limiting use to service delivery only.</li>
            <li><strong>AI analysis:</strong> Your uploaded documents and health data are processed by our AI service (OpenAI or equivalent) solely to generate your requested analysis. We configure these services in zero-data-retention mode where available.</li>
            <li><strong>Sharing you initiate:</strong> If you use our Share feature to send a report link to your doctor or caregiver, that link contains only the specific report you chose to share.</li>
            <li><strong>Legal requirements:</strong> If required by law, court order, or to prevent imminent harm.</li>
            <li><strong>Business transfers:</strong> If HealthClear AI is acquired or merges, your data may transfer to the successor entity subject to equivalent protections. We will notify you in advance.</li>
          </ul>
        </Section>

        <Section id="retention" title="7. Data Retention">
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Account data:</strong> Retained while your account is active, plus 90 days after deletion to allow recovery.</li>
            <li><strong>Uploaded documents:</strong> Retained until you delete them or delete your account.</li>
            <li><strong>Health plan data (Flexpa):</strong> Deleted within 30 days of you revoking your health plan connection, or immediately upon account deletion.</li>
            <li><strong>Analysis results:</strong> Retained until deleted by you or account closure.</li>
            <li><strong>Audit logs:</strong> Retained for 7 years as required by HIPAA.</li>
          </ul>
          <p>You can delete all your data at any time from Profile → Account → Delete Account.</p>
        </Section>

        <Section id="your-rights" title="8. Your Rights">
          <p>You have the following rights regarding your personal information:</p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li><strong>Access:</strong> Request a copy of all data we hold about you.</li>
            <li><strong>Correction:</strong> Update inaccurate account information in your Profile settings.</li>
            <li><strong>Deletion:</strong> Delete your account and all associated data. Some data may be retained where legally required (e.g., HIPAA audit logs).</li>
            <li><strong>Portability:</strong> Export your uploaded documents and analysis reports from your Profile.</li>
            <li><strong>Revoke health plan access:</strong> Disconnect your health plan at any time from Profile → Health Connections.</li>
            <li><strong>Opt out of reminders:</strong> Disable appointment/medication reminders from Profile → Notifications.</li>
          </ul>
          <p>To exercise any right, contact us at <a href={`mailto:${EMAIL}`} className="text-sky-600 underline">{EMAIL}</a>. We will respond within 30 days.</p>
        </Section>

        <Section id="security" title="9. Security">
          <p>We implement industry-standard safeguards including:</p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li>TLS 1.2+ encryption in transit for all data transfers.</li>
            <li>AES-256 encryption at rest for stored health documents and analysis results.</li>
            <li>Secrets management via Azure Key Vault — no credentials in code or configuration files.</li>
            <li>Role-based access controls limiting internal access to health data to authorized personnel only.</li>
            <li>Automatic session timeout after inactivity (HIPAA §164.312(a)(2)(iii)).</li>
            <li>Audit logging of all access to protected health information.</li>
            <li>Regular security reviews and vulnerability assessments.</li>
          </ul>
          <p>No system is 100% secure. If you believe your account has been compromised, contact us immediately at <a href={`mailto:${EMAIL}`} className="text-sky-600 underline">{EMAIL}</a>.</p>
        </Section>

        <Section id="hipaa" title="10. HIPAA Notice of Privacy Practices">
          <p>
            HealthClear AI functions as a <strong>Health Care Operations</strong> service under HIPAA. When you upload protected health information (PHI) or connect your health plan, we act as your Business Associate. We do not use or disclose your PHI except as described in this policy or as required by law.
          </p>
          <p>Your HIPAA rights include the right to:</p>
          <ul className="list-disc list-inside space-y-2 ml-2">
            <li>Receive a Notice of Privacy Practices (this section satisfies that requirement).</li>
            <li>Request restrictions on how we use or disclose your PHI.</li>
            <li>File a complaint with the U.S. Department of Health and Human Services if you believe your rights have been violated: <a href="https://www.hhs.gov/ocr/privacy/hipaa/complaints/" target="_blank" rel="noopener noreferrer" className="text-sky-600 underline">hhs.gov/ocr</a>.</li>
          </ul>
        </Section>

        <Section id="children" title="11. Children's Privacy">
          <p>Our service is not directed to children under 13. We do not knowingly collect personal information from children under 13. If you believe a child has provided us with personal information, contact us at <a href={`mailto:${EMAIL}`} className="text-sky-600 underline">{EMAIL}</a> and we will delete it promptly.</p>
        </Section>

        <Section id="changes" title="12. Changes to This Policy">
          <p>We will notify you of material changes to this policy by email (to your registered address) and by posting a notice on the app at least 30 days before changes take effect. Continued use after the effective date constitutes acceptance of the revised policy.</p>
        </Section>

        <Section id="contact" title="13. Contact Us">
          <p>For privacy questions, data requests, or to report a concern:</p>
          <ul className="list-none space-y-1 ml-2">
            <li><strong>Email:</strong> <a href={`mailto:${EMAIL}`} className="text-sky-600 underline">{EMAIL}</a></li>
            <li><strong>Mail:</strong> {COMPANY}, {ADDRESS}</li>
            <li><strong>Phone:</strong> <a href="tel:6893292013" className="text-sky-600 underline">689-329-2013</a></li>
          </ul>
        </Section>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 dark:border-slate-800 py-8 text-center">
        <p className="text-xs text-slate-400">
          © 2026 HealthClear AI · <Link to="/privacy" className="hover:text-slate-600">Privacy Policy</Link> · <Link to="/terms" className="hover:text-slate-600">Terms of Service</Link>
        </p>
        <p className="text-xs text-slate-400 mt-2">
          {ADDRESS} · <a href="tel:6893292013" className="hover:text-slate-600">689-329-2013</a>
        </p>
      </footer>
    </div>
  );
}
